import { useCallback } from "react";
import { pick as pickObject } from "diagnostics-lib/object";
import type { DashboardModelT } from "../../schema/dashboard";
import { generateExportFileName } from "diagnostics-lib/string";
import { useProject } from "../../../../../api/useProjects";
import { uniqBy } from "diagnostics-lib/array";
import { useDashboard } from "../api/dashboard";
import { MTS_ENV } from "../../../../../lib/setupEnv";
import { blobToDataUrl } from "../../../../../lib/imageDataUrl";

type ExportDashboardResult = {
  filename: string;
  hasFailedAssets: boolean;
};

export const useExportDashboardCallback = (dashboard: DashboardModelT) => {
  const project = useProject(dashboard.projectId);
  const { blocks } = useDashboard(dashboard.projectId, dashboard.id);

  return useCallback(async () => {
    let hasFailedAssets = false;
    const content = {
      ...pickObject(dashboard, ["title", "description"]),
      blocks: uniqBy(
        await Promise.all(
          blocks.map(async block => {
            const exportedBlock = {
              data: block.data,
              kind: block.kind,
              position: block.position,
              ...("config" in block ? { config: block.config } : {}),
            };

            if ((block.kind !== "DIAGRAM" && block.kind !== "IMAGE") || !block.config.url) {
              return exportedBlock;
            }

            const dataUrl = await fetch(`${MTS_ENV.COREV2_ASSETS_URL}/${block.config.url}`, {
              credentials: "include",
            })
              .then(response => {
                if (!response.ok) throw new Error("Failed to fetch embedded asset.");
                return response.blob();
              })
              .then(blobToDataUrl)
              .catch(() => {
                hasFailedAssets = true;
                return null;
              });

            if (!dataUrl) {
              return exportedBlock;
            }

            return {
              ...exportedBlock,
              config: {
                ...block.config,
                url: dataUrl,
              },
            };
          }),
        ),
        ({ position }) => position.join("-"),
      ),
    };

    const a = document.createElement("a");
    const file = new Blob([JSON.stringify(content, null, 2)], {
      type: "application/json",
    });
    const name = generateExportFileName({
      filename: "dashboard",
      projectName: project?.name ?? "",
      extension: "json",
    });
    a.href = URL.createObjectURL(file);
    a.download = name;
    a.click();
    return { filename: name, hasFailedAssets } satisfies ExportDashboardResult;
  }, [blocks, dashboard, project?.name]);
};
